import { SxProps, Theme } from "@mui/material";

// Full-width scroll area — scrollbar sits at the screen edge (like main chat).
export const mmkScrollShellStyles: SxProps<Theme> = (theme: Theme) => ({
  flex: 1,
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  overscrollBehaviorY: "contain",
  WebkitOverflowScrolling: "touch",
  backgroundColor: theme.palette.chatComponent?.mainBg ?? "background.paper",
});

// Centered content column inside the scroll shell.
export const mmkContainerInnerStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  minHeight: "100%",
  width: "100%",
  maxWidth: 1100,
  mx: "auto",
  boxSizing: "border-box",
  px: { xs: 2, sm: 3 },
  pt: { xs: 4, sm: 5 },
  pb: { xs: "110px", sm: "120px" },
};

export const mmkHeaderStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  mb: 2,
};

export const mmkHeaderTopRowStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
};

export const mmkHeaderLeftStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

export const mmkHeaderTitleBlockStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
};

export const mmkTitleStyles: SxProps<Theme> = (theme: Theme) => ({
  fontWeight: 700,
  fontSize: "18px",
  color: theme.palette.text.primary,
});

export const mmkSubtitleStyles: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.text.secondary,
  fontSize: "14px",
});

export const getMmkProgressStyles = (): SxProps<Theme> => (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    width: "100%",
    height: 10,
    borderRadius: 5,
    backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)",
    "& .MuiLinearProgress-bar": {
      borderRadius: 5,
      background:
        "linear-gradient(90deg, #60A5FA 0%, #34D399 50%, #FBBF24 100%)",
    },
  };
};

export const mmkStartSessionButtonStyles = (isDisabled: boolean) =>
  ((theme: Theme) => {
    const base = {
      flexShrink: 0,
      fontWeight: 600,
      borderRadius: "12px",
      textTransform: "none" as const,
      border: "none",
      px: { xs: 1.25, sm: 1.5, md: 2.5 },
      py: { xs: 0.75, sm: 0.875, md: 1.25 },
      fontSize: { xs: "12px", sm: "13px", md: "14px" },
      whiteSpace: "nowrap" as const,
    };
    if (isDisabled) {
      const isDark = theme.palette.mode === "dark";
      return {
        ...base,
        bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#F1F5F9",
        color: isDark ? "#94A3B8" : "#64748B",
        boxShadow: "none",
        cursor: "pointer",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#F1F5F9",
          border: "none",
          boxShadow: "none",
        },
      };
    }
    const isDark = theme.palette.mode === "dark";
    if (isDark) {
      return {
        ...base,
        bgcolor: "rgba(255,255,255,0.08)",
        color: "#E2E8F0",
        boxShadow: "none",
        border: "1px solid rgba(255,255,255,0.12)",
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "none",
        },
      };
    }
    return {
      ...base,
      bgcolor: "#FFFFFF",
      color: "#000000",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      "&:hover": {
        bgcolor: "#F8FAFC",
        border: "none",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      },
    };
  }) as SxProps<Theme>;

export const mmkFiltersWrapperStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { md: "flex-start" },
  gap: { xs: 0, md: 0.5 },
  mb: 3,
};

export const mmkFilterSectionStyles: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  flex: { md: 1 },
  minWidth: 0,
};

export const mmkFilterSectionLabelStyles: SxProps<Theme> = (theme: Theme) => ({
  display: { xs: "block", md: "none" },
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: theme.palette.text.secondary,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  mb: 0.75,
});

export const mmkCategoryTabsStyles: SxProps<Theme> = {
  display: "flex",
  gap: 0.5,
  flexWrap: "wrap",
};

export const mmkControlsSectionStyles: SxProps<Theme> = (theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  ml: { md: "auto" },
  pt: { xs: 1.5, md: 0 },
  mt: { xs: 1.5, md: 0 },
  borderTop: {
    xs: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
    md: "none",
  },
});

export const mmkControlsRowStyles: SxProps<Theme> = {
  display: "flex",
  gap: 0.5,
  flexWrap: "wrap",
};

export const getMmkTabButtonStyles =
  (isActive: boolean, category?: string): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    const isPinnedOrReview =
      category === "Pinned" ||
      category === "Review Later" ||
      category === "Mastered";
    const accentColor =
      category === "Pinned"
        ? "#CA8A04"
        : category === "Review Later"
          ? "#64748B"
          : category === "Mastered"
            ? "#22C55E"
            : null;
    const showAccentLine = !isActive && isPinnedOrReview && accentColor;
    return {
      position: "relative",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "14px",
      borderRadius: 2,
      px: 1.5,
      py: 0.75,
      bgcolor: isActive ? theme.palette.primary.main : "transparent",
      color: isActive
        ? "#FFFFFF"
        : isPinnedOrReview && accentColor
          ? accentColor
          : theme.palette.mode === "dark"
            ? theme.palette.text.secondary
            : "#334155",
      "&:hover": {
        bgcolor: isActive
          ? theme.palette.primary.dark
          : isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.04)",
      },
      ...(showAccentLine && {
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          borderRadius: 1,
          bgcolor: accentColor,
        },
      }),
    };
  };

export type MmkCardVariant = "default" | "pinned" | "review_later" | "mastered";

export const getMmkCardVariantFromItem = (item: {
  is_mastered?: boolean;
  is_pinned?: boolean;
  is_review_later?: boolean;
}): MmkCardVariant => {
  if (item.is_mastered) return "mastered";
  if (item.is_pinned) return "pinned";
  if (item.is_review_later) return "review_later";
  return "default";
};

const getMmkCardVariantColors = (
  theme: Theme,
  variant: MmkCardVariant,
  isBackFace: boolean = false,
): {
  bg: string;
  accent: string;
  borderLeft?: string;
  accentGradient?: string;
} => {
  const isDark = theme.palette.mode === "dark";
  switch (variant) {
    case "pinned":
      return isDark
        ? isBackFace
          ? {
              bg: "rgba(251, 191, 36, 0.08)",
              accent: "#F59E0B",
              borderLeft: "#F59E0B",
              accentGradient:
                "linear-gradient(180deg, #FBBF24 0%, #F59E0B 50%, #D97706 100%)",
            }
          : {
              bg: "rgba(251, 191, 36, 0.18)",
              accent: "#F59E0B",
              borderLeft: "#F59E0B",
              accentGradient:
                "linear-gradient(180deg, #FBBF24 0%, #F59E0B 50%, #D97706 100%)",
            }
        : isBackFace
          ? {
              bg: "#FEF9E7",
              accent: "#D97706",
              borderLeft: "#F59E0B",
              accentGradient:
                "linear-gradient(180deg, #FDE68A 0%, #F59E0B 50%, #D97706 100%)",
            }
          : {
              bg: "#FEF3C7",
              accent: "#D97706",
              borderLeft: "#F59E0B",
              accentGradient:
                "linear-gradient(180deg, #FDE68A 0%, #F59E0B 50%, #D97706 100%)",
            };
    case "review_later":
      return isDark
        ? isBackFace
          ? {
              bg: "rgba(148, 163, 184, 0.08)",
              accent: "#94A3B8",
              borderLeft: "#64748B",
              accentGradient:
                "linear-gradient(180deg, #94A3B8 0%, #64748B 50%, #475569 100%)",
            }
          : {
              bg: "rgba(148, 163, 184, 0.16)",
              accent: "#94A3B8",
              borderLeft: "#64748B",
              accentGradient:
                "linear-gradient(180deg, #94A3B8 0%, #64748B 50%, #475569 100%)",
            }
        : isBackFace
          ? {
              bg: "#F1F5F9",
              accent: "#64748B",
              borderLeft: "#94A3B8",
              accentGradient:
                "linear-gradient(180deg, #CBD5E1 0%, #94A3B8 50%, #64748B 100%)",
            }
          : {
              bg: "#E2E8F0",
              accent: "#64748B",
              borderLeft: "#94A3B8",
              accentGradient:
                "linear-gradient(180deg, #CBD5E1 0%, #94A3B8 50%, #64748B 100%)",
            };
    case "mastered":
      return isDark
        ? isBackFace
          ? {
              bg: "rgba(34, 197, 94, 0.08)",
              accent: "#22C55E",
              borderLeft: "#22C55E",
              accentGradient:
                "linear-gradient(180deg, #34D399 0%, #22C55E 50%, #059669 100%)",
            }
          : {
              bg: "rgba(34, 197, 94, 0.18)",
              accent: "#22C55E",
              borderLeft: "#22C55E",
              accentGradient:
                "linear-gradient(180deg, #34D399 0%, #22C55E 50%, #059669 100%)",
            }
        : isBackFace
          ? {
              bg: "#D1FAE5",
              accent: "#059669",
              borderLeft: "#10B981",
              accentGradient:
                "linear-gradient(180deg, #6EE7B7 0%, #10B981 50%, #059669 100%)",
            }
          : {
              bg: "#A7F3D0",
              accent: "#059669",
              borderLeft: "#10B981",
              accentGradient:
                "linear-gradient(180deg, #6EE7B7 0%, #10B981 50%, #059669 100%)",
            };
    default:
      return isDark
        ? isBackFace
          ? {
              bg: "rgba(40, 201, 220, 0.05)",
              accent: "#28C9DC",
              borderLeft: "#7B49C1",
              accentGradient:
                "linear-gradient(180deg, #7B49C1 0%, #28C9DC 50%, #0EA5E9 100%)",
            }
          : {
              bg: "rgba(40, 201, 220, 0.14)",
              accent: "#28C9DC",
              borderLeft: "#7B49C1",
              accentGradient:
                "linear-gradient(180deg, #7B49C1 0%, #28C9DC 50%, #0EA5E9 100%)",
            }
        : isBackFace
          ? {
              bg: "#E0F2FE",
              accent: "#28C9DC",
              borderLeft: "#7B49C1",
              accentGradient:
                "linear-gradient(180deg, #7B49C1 0%, #38BDF8 50%, #28C9DC 100%)",
            }
          : {
              bg: "#BAE6FD",
              accent: "#28C9DC",
              borderLeft: "#7B49C1",
              accentGradient:
                "linear-gradient(180deg, #7B49C1 0%, #38BDF8 50%, #28C9DC 100%)",
            };
  }
};

// Markdown emphasis color matched to the card accent (pin / know-it / default).
export const getMmkMarkdownEmphasisColor = (
  theme: Theme,
  variant: MmkCardVariant = "default",
): string => getMmkCardVariantColors(theme, variant, false).accent;

const getMmkExpandedBorder = (theme: Theme): string =>
  theme.palette.mode === "dark" ? "rgba(59, 130, 246, 0.5)" : "#94A3B8";

const getMmkDescBoxBg = (theme: Theme): string =>
  theme.palette.mode === "dark" ? "rgba(0,0,0,0.25)" : "#F1F5F9";

export const getMmkCardStyles =
  (
    isExpanded: boolean,
    isViewOnly: boolean = false,
    isSelected: boolean = false,
    variant: MmkCardVariant = "default",
    isBackFace: boolean = false,
    isAccentAnimated: boolean = false,
  ): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    const { bg, accentGradient } = getMmkCardVariantColors(
      theme,
      variant,
      isBackFace,
    );
    const expandedBorder = getMmkExpandedBorder(theme);
    const selectBorder = theme.palette.primary.main as string;
    const shadowColor =
      variant === "pinned"
        ? "251, 191, 36"
        : variant === "mastered"
          ? "34, 197, 94"
          : variant === "review_later"
            ? "100, 116, 139"
            : "123, 74, 193";
    const borderColor = isDark
      ? "rgba(255,255,255,0.15)"
      : `rgba(${shadowColor}, 0.25)`;
    return {
      position: "relative",
      overflow: "hidden",
      p: 2.5,
      borderRadius: "16px",
      backgroundColor: bg,
      border: `1px solid ${borderColor}`,
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: isAccentAnimated ? 9 : 5,
        borderRadius: "16px 0 0 16px",
        background: accentGradient,
        pointerEvents: "none",
        backgroundSize: "100% 220%",
        backgroundPosition: isAccentAnimated ? "0% 100%" : "0% 0%",
        transition:
          "width 0.35s ease, background-position 0.45s ease, filter 0.35s ease",
        filter: isAccentAnimated ? "saturate(1.25) brightness(1.08)" : "none",
      },
      boxShadow: isDark
        ? `0 4px 20px rgba(0,0,0,0.15)`
        : `0 4px 20px rgba(${shadowColor}, 0.08)`,
      cursor: isViewOnly ? "default" : "pointer",
      transition: "box-shadow 0.25s ease, outline 0.2s ease",
      ...(isExpanded && {
        outline: `2px solid ${expandedBorder}`,
        outlineOffset: 2,
      }),
      ...(isSelected &&
        !isExpanded && {
          outline: `2px solid ${selectBorder}`,
          outlineOffset: 2,
        }),
      display: "flex",
      flexDirection: "column",
      ...(!isViewOnly && {
        "&:hover": {
          boxShadow: isDark
            ? `0 4px 20px rgba(0,0,0,0.15), 0 0 20px rgba(255,255,255,0.12)`
            : `0 4px 20px rgba(${shadowColor}, 0.08), 0 0 20px rgba(${shadowColor}, 0.25)`,
        },
      }),
    };
  };

export const getMmkCardHoverWrapperStyles = (
  fullLength: boolean = false,
): SxProps<Theme> => () => ({
  position: "relative",
  minHeight: fullLength ? undefined : 220,
  height: fullLength ? "auto" : undefined,
  perspective: "1000px",
  borderRadius: "16px",
});

export const getMmkFlipCardWrapperStyles = (): SxProps<Theme> => (theme) => ({
  position: "relative",
  perspective: "1000px",
  width: "min(500px, 92vw)",
  aspectRatio: "1.586 / 1",
  mx: "auto",
  mb: 2,
  [theme.breakpoints.down("sm")]: {
    "@media (orientation: landscape)": {
      width: "min(300px, 70vw)",
    },
  },
});

const getMmkFlipCardFaceBase = (
  theme: Theme,
  variant: MmkCardVariant = "default",
  isBackFace: boolean = false,
  isAccentAnimated: boolean = false,
) => {
  const isDark = theme.palette.mode === "dark";
  const { bg, accentGradient } = getMmkCardVariantColors(
    theme,
    variant,
    isBackFace,
  );
  const cardBg = bg;
  const shadowColor =
    variant === "pinned"
      ? "251, 191, 36"
      : variant === "mastered"
        ? "34, 197, 94"
        : variant === "review_later"
          ? "100, 116, 139"
          : "123, 74, 193";
  const borderColor = isDark
    ? "rgba(255,255,255,0.15)"
    : `rgba(${shadowColor}, 0.25)`;
  return {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    backgroundColor: cardBg,
    border: `1px solid ${borderColor}`,
    overflow: "hidden" as const,
    "&::before": {
      content: '""',
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: isAccentAnimated ? 9 : 5,
      borderRadius: "16px 0 0 16px",
      background: accentGradient,
      backgroundSize: "100% 220%",
      backgroundPosition: isAccentAnimated ? "0% 100%" : "0% 0%",
      transition:
        "width 0.35s ease, background-position 0.45s ease, filter 0.35s ease",
      filter: isAccentAnimated ? "saturate(1.25) brightness(1.08)" : "none",
    },
    boxShadow: isDark
      ? `0 8px 32px rgba(0,0,0,0.08)`
      : `0 8px 32px rgba(${shadowColor}, 0.08)`,
    padding: 2.5,
    display: "flex",
    flexDirection: "column" as const,
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
    cursor: "pointer" as const,
    transition: "box-shadow 0.25s ease",
    "&:hover": {
      boxShadow: isDark
        ? `0 8px 32px rgba(0,0,0,0.08), 0 0 20px rgba(255,255,255,0.12)`
        : `0 8px 32px rgba(${shadowColor}, 0.08), 0 0 20px rgba(${shadowColor}, 0.25)`,
    },
  };
};

export const getMmkFlipCardFrontStyles =
  (
    variant: MmkCardVariant = "default",
    isAccentAnimated: boolean = false,
  ): SxProps<Theme> =>
  (theme: Theme) =>
    getMmkFlipCardFaceBase(theme, variant, false, isAccentAnimated);

export const getMmkFlipCardBackStyles =
  (
    variant: MmkCardVariant = "default",
    isAccentAnimated: boolean = false,
  ): SxProps<Theme> =>
  (theme: Theme) => ({
    ...getMmkFlipCardFaceBase(theme, variant, true, isAccentAnimated),
    transform: "rotateY(180deg)",
  });

export const getMmkExpandedCardStyles =
  (variant: MmkCardVariant = "default"): SxProps<Theme> =>
  (theme: Theme) => {
    const { bg } = getMmkCardVariantColors(theme, variant, true);
    const cardBg = bg;
    return {
      p: 2.5,
      mb: 2,
      borderRadius: "16px",
      backgroundColor: cardBg,
      boxShadow: `0 8px 32px rgba(0,0,0,0.08)`,
      cursor: "pointer",
      outline: "none",
    };
  };

export const mmkCardHeaderStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  mb: 1.5,
};

export const mmkCardHeaderLeftStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

export const getMmkCardHeadingStyles =
  (): SxProps<Theme> => (theme: Theme) => ({
    color:
      theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
  });

export const getMmkCheckboxStyles =
  (isMastered: boolean): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      cursor: "pointer",
      width: 24,
      height: 24,
      borderRadius: "50%",
      border: isMastered
        ? "none"
        : `2px solid ${isDark ? "rgba(255,255,255,0.3)" : "#CBD5E1"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: isMastered
        ? (theme.palette.primary.main as string)
        : "transparent",
    };
  };

export const getMmkSelectButtonStyles =
  (isSelected: boolean): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      cursor: "pointer",
      width: 24,
      height: 24,
      borderRadius: "6px",
      border: `2px solid ${isSelected ? (theme.palette.primary.main as string) : isDark ? "rgba(255,255,255,0.3)" : "#CBD5E1"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: isSelected
        ? (theme.palette.primary.main as string)
        : "transparent",
      flexShrink: 0,
    };
  };

export const mmkBatchActionsBarStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  py: 1,
  mb: 1,
};

export const getMmkDescBoxStyles = (): SxProps<Theme> => (theme: Theme) => {
  const descBoxBg = getMmkDescBoxBg(theme);
  return {
    p: 1.5,
    borderRadius: "12px",
    backgroundColor: descBoxBg,
    mb: 2,
  };
};

export const getMmkMainTitleStyles = (): SxProps<Theme> => (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontSize: "16px",
    fontWeight: 700,
    color: isDark ? "#FFFFFF" : "#334155",
    mb: 1.5,
  };
};

export const getMmkDescTextStyles = (): SxProps<Theme> => (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    fontSize: "14px",
    fontWeight: 400,
    color: isDark ? "#CBD5E1" : "#475569",
    lineHeight: 1.5,
  };
};

// Card face content styles (front hint, Quick answer, Full approved answer)
export const mmkCardFrontBodyStyles: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  gap: 1,
  mb: 1,
};

export const getMmkCardFrontQuestionStyles =
  (): SxProps<Theme> => (theme: Theme) => ({
    color:
      theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    textAlign: "center",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: 1.35,
  });

export const getMmkCardFrontHintStyles =
  (): SxProps<Theme> => (theme: Theme) => ({
    color: theme.palette.text.secondary,
    textAlign: "center",
    display: "block",
    fontSize: "13px",
    lineHeight: 1.4,
  });

export const getMmkCardQuestionLabelStyles =
  (): SxProps<Theme> => () => ({
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#94A3B8",
    mb: 0.75,
  });

export const getMmkCardBackQuestionStyles =
  (): SxProps<Theme> => (theme: Theme) => ({
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: 1.35,
    color:
      theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    mb: 1.5,
  });

export const getMmkQuickAnswerBoxStyles =
  (): SxProps<Theme> => (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      backgroundColor: isDark ? "rgba(45, 212, 191, 0.12)" : "#E6F7F5",
      borderRadius: "10px",
      px: 1.5,
      py: 1.25,
      mb: 1.5,
    };
  };

export const getMmkQuickAnswerHeaderStyles =
  (): SxProps<Theme> => (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      color: isDark ? "#22D3EE" : "#06B6D4",
      fontSize: "13px",
      fontWeight: 600,
      mb: 0.75,
    };
  };

export const getMmkQuickAnswerTextStyles =
  (): SxProps<Theme> => (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.5,
      color: isDark ? "#E2E8F0" : "#1E293B",
      whiteSpace: "pre-wrap",
    };
  };

export const getMmkFullAnswerToggleStyles =
  (): SxProps<Theme> => (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      width: "100%",
      border: "none",
      background: "transparent",
      cursor: "pointer",
      p: 0,
      mb: 0.5,
      textAlign: "left",
      color: isDark ? "#F1F5F9" : "#1E293B",
      "&:hover": { opacity: 0.85 },
    };
  };

export const getMmkFullAnswerTitleStyles =
  (): SxProps<Theme> => (theme: Theme) => ({
    fontSize: "14px",
    fontWeight: 700,
    color: theme.palette.mode === "dark" ? "#60A5FA" : "#2563EB",
  });

export const mmkActionsContainerStyles: SxProps<Theme> = {
  display: "flex",
  gap: { xs: 0.5, sm: 1 },
  flexWrap: "nowrap",
  flexDirection: "row",
  mt: 1,
  "& .MuiButton-root": {
    flex: "1 1 0",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    justifyContent: "center",
    "& .MuiButton-startIcon": { flexShrink: 0 },
  },
};

export const getMmkPinButtonStyles =
  (isActive?: boolean): SxProps<Theme> =>
  () => ({
    textTransform: "none",
    fontSize: { xs: "11px", sm: "13px" },
    fontWeight: 500,
    color: isActive ? "#92400E" : "#334155",
    backgroundColor: isActive ? "#FDE68A" : "#FEF3C7",
    borderRadius: "12px",
    px: { xs: 0.75, sm: 1.5 },
    minWidth: 0,
    flexShrink: 1,
    whiteSpace: "nowrap",
    border: isActive ? "1px solid #F59E0B" : "1px solid transparent",
    "&:hover": { backgroundColor: isActive ? "#FCD34D" : "#FDE68A" },
    "&.Mui-disabled": {
      color: "#9E9E9E",
      "& .MuiButton-startIcon": { color: "#9E9E9E" },
    },
  });

export const getMmkReviewLaterButtonStyles =
  (isActive?: boolean): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    return {
      textTransform: "none",
      fontSize: { xs: "11px", sm: "13px" },
      fontWeight: 500,
      color: isActive ? "#64748B" : isDark ? "#94A3B8" : "#334155",
      backgroundColor: isActive
        ? isDark
          ? "rgba(148, 163, 184, 0.2)"
          : "#E2E8F0"
        : isDark
          ? "rgba(255,255,255,0.08)"
          : "#F1F5F9",
      border: `1px solid ${isActive ? "#94A3B8" : isDark ? "#475569" : "#94A3B8"}`,
      borderRadius: "12px",
      px: { xs: 0.75, sm: 1.5 },
      minWidth: 0,
      flexShrink: 1,
      whiteSpace: "nowrap",
      "&:hover": {
        backgroundColor: isActive
          ? isDark
            ? "rgba(148, 163, 184, 0.25)"
            : "#CBD5E1"
          : isDark
            ? "rgba(255,255,255,0.12)"
            : "#F8FAFC",
      },
    };
  };

export const getMmkIKnowItButtonStyles = (
  _isActive?: boolean,
): SxProps<Theme> => ({
  textTransform: "none",
  fontSize: { xs: "11px", sm: "13px" },
  fontWeight: 500,
  color: "#FFF",
  backgroundColor: "#14b8a6",
  borderRadius: "12px",
  px: { xs: 0.75, sm: 1.5 },
  minWidth: 0,
  flexShrink: 1,
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "#0d9488" },
});

export const mmkFocusSessionNavStyles: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  mt: 2,
};

export const mmkFocusSessionCounterStyles: SxProps<Theme> = (theme: Theme) => ({
  fontWeight: 600,
  fontSize: "14px",
  color: theme.palette.text.secondary,
});

export const mmkBackToMmkButtonStyles: SxProps<Theme> = {
  textTransform: "none",
  borderRadius: "12px",
  px: 2,
};

export const mmkGridStyles: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    md: "repeat(3, 1fr)",
  },
  gap: 2,
  alignItems: "stretch",
};

// Tabbed message / content block styles (shared with chat bubble)
export const getTabbedContentMobilePadding = (theme: Theme): object => ({
  [theme.breakpoints.down("sm")]: {
    px: "12px",
  },
});

export const getTabbedBlockMobileScrollStyles = (theme: Theme): object => ({
  [theme.breakpoints.down("sm")]: {
    maxHeight: "380px",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },
});

export const getTabbedContentBoxSx = (
  theme: Theme,
  enableMobileScroll: boolean,
): object => ({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  ...(enableMobileScroll && getTabbedBlockMobileScrollStyles(theme)),
});

export const getFlashCardMobileContainerStyles = (
  theme: Theme,
  embedded: boolean,
): object => ({
  [theme.breakpoints.down("sm")]: embedded
    ? { padding: "6px", margin: 0, maxWidth: "100%" }
    : { padding: "8px", margin: "8px" },
});

// Quiz Mode styles
export const mmkQuizCardStyles: SxProps<Theme> = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    p: 2.5,
    borderRadius: "16px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(123, 74, 193, 0.25)"}`,
    backgroundColor: isDark ? "rgba(40, 201, 220, 0.05)" : "#E0F2FE",
    boxShadow: isDark
      ? "0 4px 20px rgba(0,0,0,0.15)"
      : "0 4px 20px rgba(123, 74, 193, 0.08)",
    width: "min(500px, 92vw)",
    mx: "auto",
  };
};

export const getMmkQuizOptionStyles =
  (state: "neutral" | "correct" | "wrong" | "revealed"): SxProps<Theme> =>
  (theme: Theme) => {
    const isDark = theme.palette.mode === "dark";
    const base = {
      width: "100%",
      justifyContent: "flex-start",
      textTransform: "none" as const,
      fontWeight: 500,
      fontSize: "15px",
      py: 1.5,
      px: 2,
      borderRadius: "12px",
      border: "1px solid",
    };
    if (state === "correct") {
      return {
        ...base,
        bgcolor: "rgba(34, 197, 94, 0.15)",
        borderColor: "#22C55E",
        color: "#15803D",
        "&:hover": { bgcolor: "rgba(34, 197, 94, 0.2)" },
      };
    }
    if (state === "wrong") {
      return {
        ...base,
        bgcolor: "rgba(239, 68, 68, 0.15)",
        borderColor: "#EF4444",
        color: "#B91C1C",
        "&:hover": { bgcolor: "rgba(239, 68, 68, 0.2)" },
      };
    }
    if (state === "revealed") {
      return {
        ...base,
        bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9",
        borderColor: isDark ? "rgba(255,255,255,0.12)" : "#E2E8F0",
        color: theme.palette.text.secondary,
      };
    }
    return {
      ...base,
      bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#FFFFFF",
      borderColor: isDark ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      color: theme.palette.text.primary,
      "&:hover": {
        bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#F8FAFC",
        borderColor: theme.palette.primary.main,
      },
    };
  };

export const mmkQuizSummaryCardStyles: SxProps<Theme> = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    p: 2,
    borderRadius: "12px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#E2E8F0"}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
  };
};

export const mmkQuizStartButtonStyles = (isDisabled: boolean) =>
  ((theme: Theme) => {
    const base = {
      flexShrink: 0,
      fontWeight: 600,
      borderRadius: "12px",
      textTransform: "none" as const,
      border: "none",
      px: { xs: 1.25, sm: 1.5, md: 2.5 },
      py: { xs: 0.75, sm: 0.875, md: 1.25 },
      fontSize: { xs: "12px", sm: "13px", md: "14px" },
      whiteSpace: "nowrap" as const,
    };
    if (isDisabled) {
      const isDark = theme.palette.mode === "dark";
      return {
        ...base,
        bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#F1F5F9",
        color: isDark ? "#94A3B8" : "#64748B",
        boxShadow: "none",
        cursor: "pointer",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.12)" : "#F1F5F9",
          border: "none",
          boxShadow: "none",
        },
      };
    }
    const isDark = theme.palette.mode === "dark";
    if (isDark) {
      return {
        ...base,
        bgcolor: "rgba(255,255,255,0.08)",
        color: "#E2E8F0",
        boxShadow: "none",
        border: "1px solid rgba(255,255,255,0.12)",
        "&:hover": {
          bgcolor: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "none",
        },
      };
    }
    return {
      ...base,
      bgcolor: "#FFFFFF",
      color: "#000000",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      "&:hover": {
        bgcolor: "#F8FAFC",
        border: "none",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      },
    };
  }) as SxProps<Theme>;
